import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signIn } from '../lib/auth';
import { t } from '../i18n';
import { Button, Input, Field, Alert } from '../components/ui';

const LoginView = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Only a QR check-in is worth returning to: the scan sent the patient here on
  // purpose, and the check-in has to happen once they are in. Any other page
  // RequireAuth passes along is usually just wherever the last session ended —
  // signing out re-renders that page as signed out on its way here — so every
  // other sign-in starts at Inicio.
  const from = location.state?.from;
  const redirectTo = typeof from === 'string' && from.startsWith('/checkin/') ? from : '/';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    const { error: signInError } = await signIn(email.trim(), password);
    if (signInError) {
      setError(
        signInError.message === 'Invalid login credentials'
          ? t('auth.invalidCredentials')
          : signInError.message
      );
      setLoading(false);
      return;
    }
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-indigo-900">{t('common.appName')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('auth.tagline')}</p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field label={t('auth.email')} required>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                disabled={loading}
              />
            </Field>

            <Field label={t('auth.password')} required>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                disabled={loading}
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full">
              {loading ? t('auth.loggingIn') : t('auth.login')}
            </Button>

            <p className="text-center text-sm">
              <Link to="/recuperar" className="text-indigo-600 hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </p>
          </div>

          <div className="mt-6 space-y-2 border-t border-slate-100 pt-5 text-center text-sm text-slate-600">
            <p>
              {t('auth.noAccount')}{' '}
              <Link to="/registro" className="font-semibold text-indigo-600 hover:underline">
                {t('auth.register')}
              </Link>
            </p>
            {/* Professionals do not register as patients; admin records them
                first and they set a password here. */}
            <p className="text-xs">
              {t('auth.areYouProfessional')}{' '}
              <Link to="/activar" className="font-semibold text-indigo-600 hover:underline">
                {t('auth.activateHere')}
              </Link>
              {' · '}
              <Link to="/registro-profesional" className="font-semibold text-indigo-600 hover:underline">
                {t('auth.applyHere')}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginView;
